import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, PaperclipIcon } from 'lucide-react';
export function ValueProp() {
  return <section className="py-16 sm:py-24 lg:py-32 bg-[#0a0a0a] relative overflow-hidden" data-id="element-330">
      {/* Background Texture */}
      <div className="absolute inset-0 opacity-5" data-id="element-331">
        <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1626265774643-f1943311a86b?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center mix-blend-luminosity" data-id="element-332" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-333">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center space-y-4 sm:space-y-8 mb-12 sm:mb-24" data-id="element-334">
          <h2 className="font-typewriter text-2xl sm:text-4xl text-[#ece3d0] drop-shadow-md" data-id="element-335">
            This isn't a board game.
          </h2>

          <div className="space-y-3 sm:space-y-4 font-serif-body text-lg sm:text-2xl text-[#ece3d0]/80 max-w-3xl mx-auto leading-relaxed" data-id="element-336">
            <p data-id="element-337">
              This is the night your friend group stops scrolling and starts
              thinking.
            </p>
            <p data-id="element-338">
              This is the date night that actually becomes a story you tell
              later.
            </p>
            <p data-id="element-339">This is the family evening nobody wanted to end.</p>
          </div>

          <div className="flex items-center justify-center space-x-3 text-[#8b1a1a] pt-3 sm:pt-6" data-id="element-340">
            <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-341" />
            <span className="font-typewriter text-sm sm:text-lg tracking-widest uppercase" data-id="element-342">
              2, 3, 4 hours of pure presence
            </span>
          </div>

          <p className="font-serif-body text-base sm:text-xl text-[#ece3d0]/60 max-w-2xl mx-auto italic" data-id="element-343">
            where the only thing that matters is figuring out who did it, how,
            and why.
          </p>
        </motion.div>

        {/* Evidence Board / Dossier */}
        <motion.div initial={{
        opacity: 0,
        y: 50,
        rotateX: 10
      }} whileInView={{
        opacity: 1,
        y: 0,
        rotateX: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative max-w-4xl mx-auto perspective-1000" data-id="element-344">
          {/* The Dossier Folder */}
          <div className="bg-[#ece3d0] bg-paper-texture rounded shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-6 sm:p-16 relative border border-[#d4c5b0] transform-gpu" data-id="element-345">
            {/* Tape details */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-32 h-8 bg-white/40 backdrop-blur-sm rotate-[-2deg] shadow-sm" data-id="element-346" />
            <div className="absolute -bottom-3 right-12 w-24 h-8 bg-white/40 backdrop-blur-sm rotate-[4deg] shadow-sm" data-id="element-347" />

            {/* Paperclip */}
            <PaperclipIcon className="absolute top-4 left-4 w-8 h-8 text-gray-500/50 -rotate-45" data-id="element-348" />

            {/* Classified Stamp */}
            <div className="absolute top-8 right-8 border-4 border-[#8b1a1a]/80 text-[#8b1a1a]/80 font-typewriter text-2xl sm:text-4xl font-bold px-4 py-2 rotate-[15deg] opacity-80 mix-blend-multiply" data-id="element-349">
              CLASSIFIED
            </div>

            <div className="space-y-5 sm:space-y-8 text-[#1a1a1a] relative z-10 mt-10 sm:mt-0 max-w-2xl" data-id="element-350">
              {/* Redacted Bar Accent */}
              <div className="w-20 sm:w-24 h-3 sm:h-4 bg-[#1a1a1a] mb-4 sm:mb-8" data-id="element-351" />

              <h3 className="font-typewriter text-2xl sm:text-4xl font-bold leading-tight" data-id="element-352">
                Real cases.
                <br data-id="element-353" />
                Real mystery.
                <br data-id="element-354" />
                Real tension.
              </h3>

              <div className="font-serif-body text-base sm:text-2xl leading-relaxed space-y-4 sm:space-y-6 text-[#2a2a2a]" data-id="element-355">
                <p data-id="element-356">
                  Every case in our collection is inspired by real unsolved
                  events in the United States — disappearances, crimes, and
                  investigations that law enforcement couldn't close.
                </p>
                <p data-id="element-357">
                  We took those files. We rebuilt them as immersive experiences.
                </p>
                <p className="font-semibold italic text-[#8b1a1a]" data-id="element-358">
                  And now we're putting them in your hands.
                </p>
              </div>

              {/* Bottom Redacted Bars */}
              <div className="pt-8 flex space-x-2 opacity-80" data-id="element-359">
                <div className="w-32 h-3 bg-[#1a1a1a]" data-id="element-360" />
                <div className="w-16 h-3 bg-[#1a1a1a]" data-id="element-361" />
                <div className="w-48 h-3 bg-[#1a1a1a]" data-id="element-362" />
              </div>
            </div>

            {/* Attached Photo */}
            <div className="hidden md:block absolute -right-12 bottom-12 w-48 h-48 bg-white p-2 shadow-xl rotate-[8deg] border border-gray-200" data-id="element-363">
              <img src="https://images.unsplash.com/photo-1588693959306-4120845c1965?q=80&w=400&auto=format&fit=crop" alt="Evidence" className="w-full h-full object-cover filter grayscale contrast-125" data-id="element-364" />
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-4 bg-red-500/20 backdrop-blur-sm rotate-[-5deg]" data-id="element-365" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}